import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { changeTone, formatChange, type Polarity } from './format'
import { cx } from './hooks'
import { Icon } from './Icon'
import { Sparkline } from './Sparkline'

export interface KpiCardProps {
  label: string
  /** Already formatted: "£31,450". */
  value: string
  /** Relative change against the comparison period, as a fraction. */
  change: number | null
  polarity: Polarity
  comparisonLabel?: string | null
  sparkline?: readonly number[]
  /** A drill-down; the whole tile becomes the link. */
  href?: string
  /** Shown in place of a delta when there is no comparison. */
  note?: string
  /** A secondary figure beneath the delta, already formatted: "Net after refunds £43.00". */
  detail?: string
  /** Provenance beneath the figure — whose number it is (a source badge). Not inside a linked tile: a badge is focusable. */
  badge?: ReactNode
}

const TONE_TEXT = { positive: 'favourable', negative: 'unfavourable', neutral: null } as const

/**
 * A headline metric. The delta is coloured by what the change *means*, not by
 * its sign: revenue up is green, cost per acquisition up is red, spend up is
 * neutral. That reading is also spelled out for screen readers.
 */
export function KpiCard({ label, value, change, polarity, comparisonLabel, sparkline, href, note, detail, badge }: KpiCardProps) {
  const tone = changeTone(change, polarity)
  const toneText = TONE_TEXT[tone]

  const content = (
    <>
      <span className="ci-kpi__label">{label}</span>
      <span className="ci-kpi__value">{value}</span>
      <span className="ci-kpi__delta" data-tone={tone} data-testid="kpi-delta">
        {change === null ? (
          <span className="ci-kpi__note">{note ?? 'No comparison'}</span>
        ) : (
          <>
            {change !== 0 ? <Icon name={change > 0 ? 'arrowUp' : 'arrowDown'} size={12} /> : null}
            <span className="ci-kpi__change">{formatChange(change)}</span>
            {toneText ? <span className="ci-visually-hidden"> ({toneText})</span> : null}
            {comparisonLabel ? <span className="ci-kpi__compare"> {comparisonLabel}</span> : null}
          </>
        )}
      </span>
      {detail ? <span className="ci-kpi__detail">{detail}</span> : null}
      {badge ? <span className="ci-kpi__badge">{badge}</span> : null}
      {sparkline ? <Sparkline values={sparkline} tone={tone} /> : null}
    </>
  )

  return href ? (
    <Link to={href} className="ci-kpi" data-interactive="true">
      {content}
    </Link>
  ) : (
    <div className="ci-kpi">{content}</div>
  )
}

/**
 * A headline figure the source cannot give: its label, what it reads instead ("Not live
 * yet", "Not available"), never a 0, and why. No link: there is no figure to explain.
 */
export function UnavailableKpiCard({
  label,
  value,
  note,
  badge,
  className,
}: {
  label: string
  value: string
  note: string
  /** As `KpiCard`'s: provenance or a definition beneath the figure. */
  badge?: ReactNode
  className?: string
}) {
  return (
    <div className={cx('ci-kpi', className)} data-source="not_live">
      <span className="ci-kpi__label">{label}</span>
      <span className="ci-kpi__value">{value}</span>
      <span className="ci-kpi__delta" data-tone="neutral">
        <span className="ci-kpi__note">{note}</span>
      </span>
      {badge ? <span className="ci-kpi__badge">{badge}</span> : null}
    </div>
  )
}
